import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Switch, message } from "antd";
import {
  ClientMenuCategory, CategoryItem, MenuCategoryType,
  getMenuCategories, createMenuCategory, updateMenuCategory, deleteMenuCategory,
} from "../../../utils/api/menuCategoryAPI";
import {
  ClientApartment, updateClientApartment,
} from "../../../utils/api/apartmentRentalsAPI";
import { ClientMeal, getMeals } from "../../../utils/api/mealsAPI";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  clientId: string;
  apartments: ClientApartment[];
  onApartmentsChange: (updated: ClientApartment) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENCY_OPTS = ["EUR", "BAM", "RSD"] as const;

const EMOJI_SUGGESTIONS = [
  "🍕", "🍔", "🍽️", "🥗", "🍳", "🏍️", "🚗", "🛥️", "🏄", "⛷️",
  "💆", "🧖", "🛁", "🎭", "🎵", "🏋️", "🎯", "🚴", "🌊", "🏔️",
  "📞", "📋", "🛎️", "🔑", "🧹", "🚿", "🛏️", "🍷", "☕", "🍰",
];

const TYPE_OPTS: { value: MenuCategoryType; label: string; icon: string; color: string }[] = [
  { value: "contact", label: "Kontakt", icon: "☎️", color: "#0ea5e9" },
  { value: "items", label: "Sopstvene stavke", icon: "📋", color: "#7c6ef5" },
  { value: "meals", label: "Obroci", icon: "🍽️", color: "#16a34a" },
];

const blank = (): Partial<ClientMenuCategory> => ({
  name: "", emoji: "🍴", description: "",
  type: "meals",
  contactPhone: "", contactLabel: "Kontaktirajte nas",
  items: [], currency: "EUR",
  linkedMealIds: [],
  showPrices: true,
});

// ─── Item row ─────────────────────────────────────────────────────────────────

interface ItemRowProps { item: CategoryItem; currency: string; onChange: (i: CategoryItem) => void; onRemove: () => void; }
const ItemRow: React.FC<ItemRowProps> = ({ item, currency, onChange, onRemove }) => (
  <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0edf8" }}>
    <input placeholder="Naziv stavke" value={item.name}
      onChange={(e) => onChange({ ...item, name: e.target.value })}
      style={{ flex: 2, padding: "6px 10px", border: "1.5px solid #e8e5f0", borderRadius: 7, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
    <input type="number" min={0} step={0.5} placeholder="0" value={item.price || ""}
      onChange={(e) => onChange({ ...item, price: parseFloat(e.target.value) || 0 })}
      style={{ width: 68, padding: "6px 8px", border: "1.5px solid #e8e5f0", borderRadius: 7, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
    <span style={{ fontSize: 12, color: "#9893a8", whiteSpace: "nowrap" }}>{currency}</span>
    <Switch size="small" checked={item.isAvailable} onChange={(v) => onChange({ ...item, isAvailable: v })} style={item.isAvailable ? { background: "#16a34a" } : undefined} />
    <button type="button" onClick={onRemove}
      style={{ width: 28, height: 28, border: "none", background: "#fef2f2", color: "#ef4444", borderRadius: 7, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
  </div>
);

// ─── Type selector ────────────────────────────────────────────────────────────

interface TypeSelectorProps { value: MenuCategoryType; onChange: (t: MenuCategoryType) => void; }
const TypeSelector: React.FC<TypeSelectorProps> = ({ value, onChange }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Tip kategorije</label>
    <div style={{ display: "flex", gap: 6 }}>
      {TYPE_OPTS.map((opt) => {
        const selected = value === opt.value;
        return (
          <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
            style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              padding: "10px 6px", borderRadius: 9, cursor: "pointer",
              border: `1.5px solid ${selected ? opt.color : "#e8e5f0"}`,
              background: selected ? `${opt.color}10` : "#fff",
            }}>
            <span style={{ fontSize: 18 }}>{opt.icon}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: selected ? opt.color : "#6b7280", textAlign: "center" }}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

// ─── Edit modal ───────────────────────────────────────────────────────────────

interface EditModalProps { initial: Partial<ClientMenuCategory> | null; meals: ClientMeal[]; onSave: (d: Partial<ClientMenuCategory>) => Promise<void>; onClose: () => void; }
const EditModal: React.FC<EditModalProps> = ({ initial, meals, onSave, onClose }) => {
  const [form, setForm] = useState<Partial<ClientMenuCategory>>(initial ?? blank());
  const [saving, setSaving] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [catType, setCatType] = useState<MenuCategoryType>(initial?.type ?? "meals");
  const [showPrices, setShowPrices] = useState(initial?.showPrices !== false);
  const [selectedMealIds, setSelectedMealIds] = useState<string[]>(initial?.linkedMealIds ?? []);

  const set = (key: keyof ClientMenuCategory, val: any) => setForm((p) => ({ ...p, [key]: val }));

  const toggleMeal = (id: string) =>
    setSelectedMealIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const addItem = () => setForm((p) => ({ ...p, items: [...(p.items || []), { name: "", price: 0, currency: p.currency || "EUR", isAvailable: true }] }));
  const updateItem = (idx: number, item: CategoryItem) => setForm((p) => { const items = [...(p.items || [])]; items[idx] = item; return { ...p, items }; });
  const removeItem = (idx: number) => setForm((p) => ({ ...p, items: (p.items || []).filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.name?.trim()) { message.warning("Unesite naziv kategorije"); return; }
    const phone = (form.contactPhone || "").trim();
    const payload: Partial<ClientMenuCategory> = {
      ...form,
      type: catType,
      // Contact phone is optional add-on metadata, kept regardless of the primary type.
      contactPhone: phone || null,
      contactLabel: phone ? (form.contactLabel || "Kontaktirajte nas") : "Kontaktirajte nas",
      items: catType === "items" ? (form.items || []) : [],
      linkedMealIds: catType === "meals" ? selectedMealIds : [],
      showPrices: catType === "contact" ? true : showPrices,
    };
    setSaving(true);
    try { await onSave(payload); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1200, overflowY: "auto", padding: "40px 16px" }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520, boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "18px 22px 16px", borderBottom: "1px solid #f0edf8", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#1a1520" }}>{initial ? "Uredi kategoriju" : "Nova kategorija"}</span>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: "#9893a8", cursor: "pointer" }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Emoji + Name */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ position: "relative" }}>
              <button type="button" onClick={() => setShowEmoji(!showEmoji)}
                style={{ width: 52, height: 52, border: "1.5px solid #e8e5f0", borderRadius: 10, fontSize: 26, background: "#f8f7ff", cursor: "pointer" }}>
                {form.emoji || "🍴"}
              </button>
              {showEmoji && (
                <div style={{ position: "absolute", top: 58, left: 0, background: "#fff", border: "1.5px solid #e8e5f0", borderRadius: 12, padding: 10, display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 4, zIndex: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  {EMOJI_SUGGESTIONS.map((e) => (
                    <button key={e} type="button" onClick={() => { set("emoji", e); setShowEmoji(false); }}
                      style={{ width: 36, height: 36, border: "none", background: "none", fontSize: 20, cursor: "pointer", borderRadius: 6 }}>{e}</button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Naziv kategorije *</label>
              <input placeholder="npr. Hrana, Vožnja kvadom…" value={form.name || ""} onChange={(e) => set("name", e.target.value)}
                style={{ padding: "10px 12px", border: "1.5px solid #e8e5f0", borderRadius: 9, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
            </div>
          </div>

          {/* Description */}
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Opis (opciono)</label>
            <input placeholder="Kratki opis koji gost vidi" value={form.description || ""} onChange={(e) => set("description", e.target.value)}
              style={{ padding: "10px 12px", border: "1.5px solid #e8e5f0", borderRadius: 9, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
          </div>

          {/* Type selector */}
          <TypeSelector value={catType} onChange={setCatType} />

          {/* Show prices toggle — irrelevant for contact-only categories */}
          {catType !== "contact" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: showPrices ? "#f0fdf4" : "#fef9c3", borderRadius: 9, border: `1.5px solid ${showPrices ? "#86efac" : "#fde047"}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1520" }}>Prikaži cijene gostima</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                  {showPrices ? "Gosti vide cijene stavki" : "Cijene su skrivene (npr. uključene u apartman)"}
                </div>
              </div>
              <Switch checked={showPrices} onChange={setShowPrices} style={showPrices ? { background: "#16a34a" } : undefined} />
            </div>
          )}

          {/* Primary content block — exactly one, matching the selected type */}
          {catType === "contact" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Broj telefona</label>
                <input type="tel" placeholder="+387 6x xxx xxx" value={form.contactPhone || ""} onChange={(e) => set("contactPhone", e.target.value)}
                  style={{ padding: "9px 12px", border: "1.5px solid #e8e5f0", borderRadius: 8, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Tekst dugmeta poziva</label>
                <input placeholder="npr. Rezervišite vožnju" value={form.contactLabel || ""} onChange={(e) => set("contactLabel", e.target.value)}
                  style={{ padding: "9px 12px", border: "1.5px solid #e8e5f0", borderRadius: 8, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
              </div>
            </div>
          )}

          {catType === "items" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: "#6b7280" }}>Stavke koje gost može naručiti</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select value={form.currency || "EUR"} onChange={(e) => set("currency", e.target.value)}
                    style={{ padding: "4px 8px", border: "1.5px solid #e8e5f0", borderRadius: 6, fontSize: 12, fontFamily: "inherit", outline: "none" }}>
                    {CURRENCY_OPTS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <button type="button" onClick={addItem}
                    style={{ padding: "4px 12px", background: "#7c6ef5", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    + Dodaj
                  </button>
                </div>
              </div>
              {(form.items || []).length === 0 && (
                <div style={{ textAlign: "center", padding: "16px", color: "#9893a8", fontSize: 12, background: "#f8f7ff", borderRadius: 8 }}>
                  Klikni "+ Dodaj" da dodaš prvu stavku
                </div>
              )}
              {(form.items || []).map((item, idx) => (
                <ItemRow key={idx} item={item} currency={form.currency || "EUR"}
                  onChange={(updated) => updateItem(idx, updated)} onRemove={() => removeItem(idx)} />
              ))}
            </div>
          )}

          {catType === "meals" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                Odaberi koje obroke gost vidi u ovoj kategoriji. Ostavi sve neoznačene da se prikažu svi.
              </div>
              {meals.filter((m) => m.isActive).length === 0 ? (
                <div style={{ padding: "10px 12px", background: "#f8f7ff", borderRadius: 8, fontSize: 12, color: "#9893a8" }}>
                  Nema aktivnih obroka. Dodaj ih u tabu <strong>Obroci</strong>.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {meals.filter((m) => m.isActive).map((m) => {
                    const selected = selectedMealIds.includes(m._id);
                    return (
                      <button key={m._id} type="button" onClick={() => toggleMeal(m._id)}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1.5px solid ${selected ? "#16a34a" : "#e8e5f0"}`, borderRadius: 9, background: selected ? "#f0fdf4" : "#fff", cursor: "pointer", textAlign: "left", width: "100%" }}>
                        <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selected ? "#16a34a" : "#d1d5db"}`, background: selected ? "#16a34a" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                          {selected && <span style={{ color: "#fff", fontSize: 12, lineHeight: 1 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: selected ? "#15803d" : "#374151" }}>{m.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {selectedMealIds.length === 0 && meals.filter((m) => m.isActive).length > 0 && (
                <div style={{ fontSize: 11, color: "#9893a8", paddingLeft: 2 }}>
                  Svi obroci će biti prikazani (nijedan nije odabran)
                </div>
              )}
            </div>
          )}

          {/* Optional "also show a phone number" add-on — common alongside items/meals in practice */}
          {catType !== "contact" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "10px 12px", background: "#f8f7ff", borderRadius: 9, border: "1.5px solid #e8e5f0" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>☎️ Broj telefona (opciono)</div>
              <input type="tel" placeholder="+387 6x xxx xxx" value={form.contactPhone || ""} onChange={(e) => set("contactPhone", e.target.value)}
                style={{ padding: "8px 10px", border: "1.5px solid #e8e5f0", borderRadius: 7, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
              {(form.contactPhone || "").trim() && (
                <input placeholder="Tekst dugmeta (npr. Pozovite restoran)" value={form.contactLabel || ""} onChange={(e) => set("contactLabel", e.target.value)}
                  style={{ padding: "8px 10px", border: "1.5px solid #e8e5f0", borderRadius: 7, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 22px", borderTop: "1px solid #f0edf8", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose}
            style={{ padding: "9px 20px", border: "1.5px solid #e8e5f0", borderRadius: 9, background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Odustani
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            style={{ padding: "9px 22px", border: "none", borderRadius: 9, background: "#7c6ef5", color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Snima…" : "Snimi"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Category card ────────────────────────────────────────────────────────────

interface CatCardProps { cat: ClientMenuCategory; onEdit: () => void; onDelete: () => void; onToggleActive: () => void; }
const CatCard: React.FC<CatCardProps> = ({ cat, onEdit, onDelete, onToggleActive }) => {
  const typeOpt = TYPE_OPTS.find((t) => t.value === cat.type);
  let badgeLabel = typeOpt?.label ?? "";
  if (cat.type === "contact") badgeLabel = "☎ Kontakt";
  else if (cat.type === "items") badgeLabel = `📋 ${(cat.items || []).length} stavki`;
  else if (cat.type === "meals") {
    const n = (cat.linkedMealIds || []).length;
    badgeLabel = n === 0 ? "🍽️ Svi obroci" : `🍽️ ${n} ${n === 1 ? "obrok" : "obroka"}`;
  }
  const badgeColor = typeOpt?.color ?? "#9893a8";
  const hasContactAddon = cat.type !== "contact" && !!cat.contactPhone;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "#fff", border: "1.5px solid #e8e5f0", borderRadius: 12 }}>
      <span style={{ fontSize: 26, flexShrink: 0 }}>{cat.emoji}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1520" }}>{cat.name}</div>
        {cat.description && <div style={{ fontSize: 11, color: "#9893a8", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cat.description}</div>}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 5 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: badgeColor, background: `${badgeColor}15`, padding: "2px 7px", borderRadius: 5 }}>{badgeLabel}</span>
          {hasContactAddon && <span style={{ fontSize: 11, fontWeight: 600, color: "#0ea5e9", background: "#0ea5e915", padding: "2px 7px", borderRadius: 5 }}>☎ Kontakt</span>}
        </div>
      </div>
      <Switch size="small" checked={cat.isActive} onChange={onToggleActive} />
      <button type="button" onClick={onEdit}
        style={{ padding: "5px 10px", border: "1.5px solid #e8e5f0", borderRadius: 7, background: "#f8f7ff", color: "#5b50d6", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
        Uredi
      </button>
      <button type="button" onClick={onDelete}
        style={{ width: 30, height: 30, border: "1.5px solid #fee2e2", borderRadius: 7, background: "#fff", color: "#ef4444", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        ×
      </button>
    </div>
  );
};

// ─── Apartment row ────────────────────────────────────────────────────────────

interface AptRowProps { apt: ClientApartment; categories: ClientMenuCategory[]; saving: string | null; onToggle: (apt: ClientApartment, catId: string) => void; }
const AptRow: React.FC<AptRowProps> = ({ apt, categories, saving, onToggle }) => {
  const [showQr, setShowQr] = useState(false);
  const menuUrl = `${window.location.origin}/menu/${apt._id}`;

  const isLinked = (catId: string) =>
    (apt.menuCategories || []).some((mc) => mc.categoryId === catId && mc.isActive);

  const handlePrint = () => {
    const win = window.open("", "_blank");
    if (!win) return;
    const svgEl = document.getElementById(`qr-${apt._id}`);
    win.document.write(`<html><head><title>QR — ${apt.name}</title><style>body{font-family:Arial,sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0;}h2{font-size:22px;margin-bottom:6px;}p{font-size:13px;color:#6b7280;margin:0 0 18px;}.url{margin-top:14px;font-size:11px;color:#9893a8;word-break:break-all;max-width:260px;text-align:center;}</style></head><body><h2>${apt.name}</h2><p>Skenirajte za naručivanje</p>${svgEl?.outerHTML ?? ""}<div class="url">${menuUrl}</div></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  return (
    <div style={{ border: "1.5px solid #e8e5f0", borderRadius: 12, overflow: "hidden", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "#f8f7ff", borderBottom: categories.length > 0 ? "1px solid #e8e5f0" : "none" }}>
        <span style={{ fontSize: 15 }}>🏠</span>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#1a1520", flex: 1 }}>{apt.name}</span>
        <span style={{ fontSize: 11, color: "#9893a8" }}>{apt.location}</span>
        <button type="button" onClick={() => setShowQr(true)}
          style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", border: "1.5px solid #7c6ef5", borderRadius: 7, background: "#f5f3ff", color: "#5b50d6", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h7v7H3zm1 1v5h5V4zm1 1h3v3H5zm8-2h7v7h-7zm1 1v5h5V4zm1 1h3v3h-3zm-9 8H3v7h7v-7zm-1 1v5H4v-5zm-4 1h3v3H5v-3zm10 5v-5h-1v1h-1v-1h-1v2h1v1h-1v1h1v1h1v-1h1v1h1v-2zm-1-2h-1v-1h1zm0-2v1h-1v-1zm2 4h1v1h-1zm0-2h1v1h-1zm2 0h-1v-1h1v-1h1v2zm0 2v1h-1v-1zm1-4h-1v-1h1v1z"/></svg>
          QR
        </button>
      </div>

      {categories.map((cat) => (
        <div key={cat._id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 16px", borderBottom: "1px solid #f0edf8", background: isLinked(cat._id) ? "rgba(91,80,214,0.03)" : "#fff" }}>
          <span style={{ fontSize: 18 }}>{cat.emoji}</span>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: isLinked(cat._id) ? "#1a1520" : "#9893a8" }}>{cat.name}</span>
          <Switch size="small" checked={isLinked(cat._id)} loading={saving === apt._id + cat._id} onChange={() => onToggle(apt, cat._id)} />
        </div>
      ))}

      {categories.length === 0 && (
        <div style={{ padding: "12px 16px", fontSize: 12, color: "#9893a8" }}>Nema kategorija</div>
      )}

      {showQr && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}
          onClick={() => setShowQr(false)}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 14, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", maxWidth: 340, width: "90%" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#1a1520" }}>{apt.name}</div>
            <div style={{ fontSize: 12, color: "#6b7280", textAlign: "center" }}>Skenirajte QR kod za naručivanje</div>
            <QRCodeSVG id={`qr-${apt._id}`} value={menuUrl} size={200} bgColor="#ffffff" fgColor="#1a1520" level="M" />
            <div style={{ fontSize: 10, color: "#9893a8", wordBreak: "break-all", textAlign: "center", maxWidth: 220 }}>{menuUrl}</div>
            <div style={{ display: "flex", gap: 10, width: "100%" }}>
              <button type="button" onClick={handlePrint}
                style={{ flex: 1, padding: "10px 0", border: "1.5px solid #7c6ef5", borderRadius: 10, background: "#7c6ef5", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Štampaj
              </button>
              <button type="button" onClick={() => setShowQr(false)}
                style={{ flex: 1, padding: "10px 0", border: "1.5px solid #e5e2f0", borderRadius: 10, background: "#f8f7ff", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const MenuTab: React.FC<Props> = ({ clientId, apartments, onApartmentsChange }) => {
  const [categories, setCategories] = useState<ClientMenuCategory[]>([]);
  const [meals, setMeals] = useState<ClientMeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState<ClientMenuCategory | null | "new">(null);
  const [aptSaving, setAptSaving] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getMenuCategories(clientId),
      getMeals(clientId),
    ])
      .then(([catRes, mealRes]) => { setCategories(catRes.data); setMeals(mealRes.data); })
      .catch(() => message.error("Greška pri učitavanju"))
      .finally(() => setLoading(false));
  }, [clientId]);

  const handleSave = async (data: Partial<ClientMenuCategory>) => {
    if (editTarget === "new") {
      const res = await createMenuCategory(clientId, data);
      setCategories((p) => [...p, res.data]);
      message.success("Kategorija dodana");
    } else if (editTarget) {
      const res = await updateMenuCategory(editTarget._id, data);
      setCategories((p) => p.map((c) => c._id === editTarget._id ? res.data : c));
      message.success("Kategorija ažurirana");
    }
    setEditTarget(null);
  };

  const handleDelete = async (id: string) => {
    await deleteMenuCategory(id);
    setCategories((p) => p.filter((c) => c._id !== id));
    message.success("Kategorija obrisana");
  };

  const handleToggleActive = async (cat: ClientMenuCategory) => {
    const res = await updateMenuCategory(cat._id, { isActive: !cat.isActive });
    setCategories((p) => p.map((c) => c._id === cat._id ? res.data : c));
  };

  const handleToggleAptCategory = async (apt: ClientApartment, catId: string) => {
    const key = apt._id + catId;
    setAptSaving(key);
    try {
      const current = apt.menuCategories || [];
      const existing = current.find((mc) => mc.categoryId === catId);
      const updated = existing
        ? current.map((mc) => mc.categoryId === catId ? { ...mc, isActive: !mc.isActive } : mc)
        : [...current, { categoryId: catId, isActive: true }];
      const res = await updateClientApartment(apt._id, { menuCategories: updated } as any);
      onApartmentsChange(res.data);
    } catch {
      message.error("Greška pri snimanju");
    } finally {
      setAptSaving(null);
    }
  };

  return (
    <div style={{ padding: "24px 0" }}>

      {/* Categories */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1520" }}>Kategorije menija</div>
            <div style={{ fontSize: 12, color: "#9893a8", marginTop: 2 }}>Svaka kategorija ima tačno jedan tip sadržaja: kontakt, sopstvene stavke ili obroci</div>
          </div>
          <button type="button" onClick={() => setEditTarget("new")}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", border: "none", borderRadius: 9, background: "#7c6ef5", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Nova kategorija
          </button>
        </div>

        {loading && <div style={{ padding: 24, textAlign: "center", color: "#9893a8", fontSize: 13 }}>Učitavanje…</div>}

        {!loading && categories.length === 0 && (
          <div style={{ padding: "32px 24px", textAlign: "center", background: "#f8f7ff", borderRadius: 12, border: "1.5px dashed #c4bfde" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🗂️</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1520", marginBottom: 6 }}>Nema kategorija</div>
            <div style={{ fontSize: 12, color: "#9893a8" }}>Dodajte kategorije poput "Hrana", "Vožnja kvadom", "Recepcija"…</div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {categories.map((cat) => (
            <CatCard key={cat._id} cat={cat}
              onEdit={() => setEditTarget(cat)}
              onDelete={() => handleDelete(cat._id)}
              onToggleActive={() => handleToggleActive(cat)} />
          ))}
        </div>
      </div>

      {/* Apartments */}
      <div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1520" }}>Kategorije po apartmanu</div>
          <div style={{ fontSize: 12, color: "#9893a8", marginTop: 2 }}>Aktivirajte kategorije po apartmanu i generišite QR kod</div>
        </div>
        {apartments.map((apt) => (
          <AptRow key={apt._id} apt={apt} categories={categories.filter((c) => c.isActive)}
            saving={aptSaving} onToggle={handleToggleAptCategory} />
        ))}
        {apartments.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "#9893a8", fontSize: 13 }}>Nema apartmana</div>}
      </div>

      {editTarget !== null && (
        <EditModal initial={editTarget === "new" ? null : editTarget} meals={meals} onSave={handleSave} onClose={() => setEditTarget(null)} />
      )}
    </div>
  );
};

export default MenuTab;
